import { Injectable } from '@angular/core';
import { BrevoService } from '../../../services/brevo';
import { FormControl, FormGroup } from '@angular/forms';
import { Subject, throwError } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  exhaustMap,
  filter,
  map,
  mapTo,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { BrevoResponseCodesEnum } from '../../../shared/constants';
import { FlightItinerary } from '../interfaces';
import { UpsellService } from '../../../services/upsell/upsell.service';
import { AbandonedCartEventsEnum } from '../../checkout-page/constants';
import { AbandonedCartUtils } from '../utils/abandoned-cart.utils';

@Injectable({
  providedIn: 'root',
})
export class AdsConsentTrackerService {
  isUserIdentified = false;

  constructor(
    private readonly brevoService: BrevoService,
    private readonly upsellService: UpsellService
  ) {}

  // Orchestrates the full tracking flow using form data and dependencies
  trackAdsConsentFullFlow({
    form,
    destroy$,
    itinerary,
  }: {
    form: FormGroup;
    destroy$: Subject<void>;
    itinerary: FlightItinerary;
  }) {
    const termsForm = form.get('terms') as FormGroup | null;
    const contactForm = form.get('contact') as FormGroup | null;

    if (!termsForm || !contactForm) return;

    this.trackAdsConsent({
      termsForm,
      contactForm,
      destroy$,
      buildUserAttributes: (cf, email, isConsented) =>
        this.buildUserAttributes(cf, email, isConsented),
      identifyUserAndTrack: (isConsented, email, attributes) =>
        this.identifyUserAndTrack(isConsented, email, attributes, itinerary),
    });
  }

  // Subscribes to the consent checkbox and triggers user tracking based on its changes
  private trackAdsConsent({
    termsForm,
    contactForm,
    destroy$,
    buildUserAttributes,
    identifyUserAndTrack,
  }: {
    termsForm: FormGroup;
    contactForm: FormGroup;
    destroy$: Subject<void>;
    buildUserAttributes: (contactForm: FormGroup, email: string, isConsented: boolean) => any;
    identifyUserAndTrack: (isConsented: boolean, email: string, attributes: unknown) => void;
  }) {
    const adsConsentCtrl = termsForm.get('adsConsent') as FormControl<boolean> | null;
    const emailCtrl = contactForm.get('email') as FormControl<string> | null;
    if (!adsConsentCtrl || !emailCtrl) return;

    adsConsentCtrl.valueChanges
      .pipe(
        debounceTime(300), // Avoid rapid toggles
        distinctUntilChanged(), // Ignore repeated values
        filter(() => emailCtrl.valid), // Ensure email is valid
        takeUntil(destroy$), // Unsubscribe on component destroy
        map((checked) => ({
          checked,
          email: emailCtrl.value.trim(),
          attributes: buildUserAttributes(contactForm, emailCtrl.value.trim(), checked),
        })),
        tap(({ checked, email, attributes }) => {
          // If user is already identified, just track the event
          if (this.isUserIdentified) identifyUserAndTrack(checked, email, attributes);
        }),
        filter(() => !this.isUserIdentified), // Only run contact creation if user is not yet identified
        exhaustMap(({ checked, email, attributes }) =>
          this.brevoService.findContact(email).pipe(
            take(1),
            catchError((err) => {
              if (err?.code === 404 || err?.code === BrevoResponseCodesEnum.NOT_FOUND) {
                // If contact does not exist, create it
                return this.brevoService
                  .createContact({
                    email,
                    attributes,
                  })
                  .pipe(take(1));
              }

              return throwError(() => err);
            }),
            mapTo({ checked, email, attributes }) // Continue with same structure
          )
        )
      )
      .subscribe({
        next: ({ checked, email, attributes }) => {
          // First time identification + event tracking
          identifyUserAndTrack(checked, email, attributes);
        },
        error: (err) => console.error('Error en flujo de consentimiento:', err),
      });
  }

  // Builds a user attributes object based on form data and consent state
  private buildUserAttributes(
    contactForm: FormGroup,
    email: string,
    isConsented: boolean
  ): unknown {
    const name = contactForm.get('name')?.value ?? '';
    const lastname = contactForm.get('lastname')?.value ?? '';
    const phoneCode = contactForm.get('phoneCode')?.value ?? '';
    const phoneNumber = contactForm.get('phoneNumber')?.value ?? '';
    const fullPhone = `${phoneCode?.value || '+51'}${phoneNumber}`;
    return {
      ...this.brevoService.contact.attributes,
      EMAIL: email,
      NOMBRE: name,
      APELLIDOS: lastname,
      SMS: fullPhone,
      DATA_POLICY: isConsented,
      FUENTE: 'BOOKING',
    };
  }

  // Identifies the user in Brevo and sends the corresponding tracking event
  private identifyUserAndTrack(
    isConsented: boolean,
    email: string,
    attributes: unknown,
    itinerary: FlightItinerary
  ) {
    this.brevoService.contact = { email, attributes };
    this.brevoService
      .identifyUser(email, attributes)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.isUserIdentified = true;

          // Select the event type based on consent
          const event = isConsented
            ? AbandonedCartEventsEnum.CART_UPDATED
            : AbandonedCartEventsEnum.ORDER_COMPLETED;

          const upsell = this.upsellService.getSelectedUpsell() ?? undefined;

          const eventData = isConsented
            ? AbandonedCartUtils.buildEventData(email, itinerary, upsell)
            : undefined;

          // Send the selected event with user context
          this.brevoService.trackEvent(event, eventData).pipe(take(1)).subscribe();
        },
        error: (err) => console.error('Falló identifyUser:', err),
      });
  }
}
